import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthQuery } from '@app/auth/auth.query';
import { TokenService } from '@app/@core/services/token.service';
import { environment } from '@env/environment';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private router: Router | null = null;

  constructor(
    private authQuery: AuthQuery,
    private tokenService: TokenService,
    private injector: Injector
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Don't intercept refresh token requests to avoid infinite loops
    if (request.url.includes('/auth/refresh')) {
      return next.handle(request);
    }

    // Don't add token to login requests (they don't need it)
    const isLoginRequest = request.url.includes('/auth/login') || request.url.includes('/auth/monitoring/login');
    
    // Email verification endpoints - these can work with or without token, so skip token refresh
    const isEmailVerificationRequest = request.url.includes('/auth/send-email-verification') || 
                                       request.url.includes('/auth/verify-email-code');
    
    // Protected API endpoints (v5) and monitoring auth endpoints (except login)
    const isProtectedApiUrl = request.url.includes('/v5/') || request.url.includes('/api/v5/');
    const isMonitoringAuthUrl = request.url.includes('/auth/monitoring/') && !isLoginRequest;
    
    if ((isProtectedApiUrl || isMonitoringAuthUrl) && !isLoginRequest && !isEmailVerificationRequest) {
      // Check if token needs refresh before making request (only for protected API)
      // Skip token refresh for email verification endpoints - they can work without refresh token
      if (isProtectedApiUrl && this.tokenService.shouldRefreshToken()) {
        return this.handleTokenRefresh(request, next);
      }
      
      if (this.authQuery.access_token) {
        request = request.clone({
          setHeaders: {
            Authorization: `Bearer ${this.authQuery.access_token}`,
          },
        });
      }
    } else if (isEmailVerificationRequest && this.authQuery.access_token) {
      // For email verification, add token if available but don't require refresh
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${this.authQuery.access_token}`,
        },
      });
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Don't try to refresh token for email verification endpoints
        if (error.status === 401 && isProtectedApiUrl && !isEmailVerificationRequest) {
          return this.handleTokenRefresh(request, next);
        }
        return throwError(error);
      })
    );
  }

  private handleTokenRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.tokenService.refreshToken().pipe(
        switchMap((response: any) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(response.access_token);
          
          // Retry the original request with new token
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${response.access_token}`,
            },
          });
          
          return next.handle(request);
        }),
        catchError((error) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(null);
          
          // If no refresh token available, try to proceed with existing token or without token
          // This allows endpoints that don't strictly require auth to work
          if (error === 'No refresh token available' || (typeof error === 'string' && error.includes('No refresh token'))) {
            console.log('JwtInterceptor: No refresh token available, attempting request with existing token or without token');
            // Try to proceed with existing token if available, otherwise without token
            if (this.authQuery.access_token) {
              request = request.clone({
                setHeaders: {
                  Authorization: `Bearer ${this.authQuery.access_token}`,
                },
              });
            }
            return next.handle(request);
          }
          
          // If refresh failed with 401, redirect to login
          if (error.status === 401) {
            this.redirectToLogin();
          }
          
          return throwError(error);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${token}`,
            },
          });
          return next.handle(request);
        })
      );
    }
  }

  private redirectToLogin(): void {
    // Lazy inject Router to avoid circular dependency
    if (!this.router) {
      this.router = this.injector.get(Router);
    }
    
    // Only redirect if not already on login page
    if (this.router && !this.router.url.includes('/login')) {
      console.log('TokenService: Redirecting to login due to failed refresh');
      this.router.navigate(['/login'], {
        queryParams: { 
          redirect: this.router.url 
        },
        replaceUrl: false
      });
    }
  }
}
